
import React from "react";
import { format } from "date-fns";
import { useQuery } from '@tanstack/react-query';
import { getUnitLabel } from "@/types/unit-types";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatCurrency } from "@/lib/utils";
import { supabase } from '@/lib/supabase';
import RelatedEstimatePreviewCard from "./RelatedEstimatePreviewCard";

interface InvoicePreviewProps {
  invoice: any;
  lineItems: any[];
  company?: any;
  RelatedEstimateCard?: React.ReactNode;
  pdfCompatible?: boolean;
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'draft':
      return <Badge variant="outline">Draft</Badge>;
    case 'sent':
      return <Badge variant="secondary">Sent</Badge>;
    case 'paid':
      return <Badge variant="default" className="bg-green-600">Paid</Badge>;
    case 'overdue':
      return <Badge variant="destructive">Overdue</Badge>;
    case 'cancelled':
      return <Badge variant="destructive">Cancelled</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

const InvoicePreview: React.FC<InvoicePreviewProps> = ({ invoice, lineItems, company: companyProp, RelatedEstimateCard, pdfCompatible = false }) => {
  // Get company info if present - prefer explicit prop, fallback to joined data
  const company = companyProp || invoice.company || invoice.companies;
  // Get customer info if present
  const customer = invoice.customers;

  // Fetch company VAT status and rate
  const { data: companyVatData } = useQuery({
    queryKey: ['companyVat', invoice?.company_id],
    queryFn: async () => {
      if (!invoice?.company_id) return null;
      
      const { data, error } = await supabase
        .from('companies')
        .select('is_vat_registered, "VAT Rate"')
        .eq('id', invoice.company_id)
        .single();

      if (error) {
        console.error('Error fetching company VAT status:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!invoice?.company_id,
  });

  // Only show VAT if company is VAT registered and tax percentage > 0
  const showVat = companyVatData?.is_vat_registered && invoice.tax_percentage > 0;
  
  // Use the actual tax percentage from the invoice, fallback to company rate, then to 15%
  const displayVatPercentage = invoice.tax_percentage || companyVatData?.["VAT Rate"] || 15;

  // Helper to build customer address lines
  const getFormattedAddress = (entity: any) => {
    if (!entity) return [];
    const lines = [];
    if (entity.street_address) lines.push(entity.street_address);
    if (entity.suburb) lines.push(entity.suburb);
    if (entity.city) lines.push(entity.city);
    if (entity.postal_code) lines.push(entity.postal_code);
    return lines;
  };

  // Determine if customer is commercial
  const isCommercialCustomer = customer?.customer_type === 'commercial';

  // Get customer display name
  const getCustomerDisplayName = () => {
    if (!customer) return null;
    if (isCommercialCustomer && customer.business_name) {
      return customer.business_name;
    }
    return `${customer.name} ${customer.last_name}`;
  };

  // Get customer contact info
  const getCustomerEmail = () => {
    if (!customer) return null;
    if (isCommercialCustomer && customer.business_email) {
      return customer.business_email;
    }
    return customer.emails?.length > 0 ? customer.emails[0] : null;
  };

  const getCustomerPhone = () => {
    if (!customer) return null;
    if (isCommercialCustomer && customer.business_phone) {
      return customer.business_phone;
    }
    return customer.cell_numbers?.length > 0 ? customer.cell_numbers[0] : null;
  };

  return (
    <div className="bg-white text-black min-h-full p-8 pb-16 flex flex-col gap-10 max-w-5xl mx-auto">
      {/* Header row shows invoice number and status, plus dates */}
      <div className="flex flex-wrap justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <div className="text-2xl font-bold text-primary">Invoice {invoice.invoice_number || 'INV-PENDING'}</div>
          {getStatusBadge(invoice.status)}
        </div>
        <div className="text-right space-y-1">
          {/* Dates */}
          <div className="text-gray-600 text-xs">
            Issued: {format(new Date(invoice.created_at), "dd MMMM yyyy")}
          </div>
          {invoice.due_date && (
            <div className="text-gray-600 text-xs">
              Due: <span className={
                new Date() > new Date(invoice.due_date + 'T23:59:59') && invoice.status !== 'paid'
                  ? "text-red-600 font-bold"
                  : ""
              }>
                {format(new Date(invoice.due_date), "dd MMMM yyyy")}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" id="invoice-preview-content">
        {/* -- Left Column: Invoice/Customer/Company -- */}
        <div className="md:col-span-1 flex flex-col gap-4">
          {/* Company info */}
          <Card className="mb-3">
            <CardHeader>
              <CardTitle className="text-primary text-lg">Company Info</CardTitle>
            </CardHeader>
            <CardContent className="pb-2">
              {company?.name && <div className="font-semibold">{company.name}</div>}
              {getFormattedAddress(company).length > 0 && (
                <div className="text-sm text-gray-700">
                  {getFormattedAddress(company).map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              )}
              {company?.email && <div className="text-sm">{company.email}</div>}
              {company?.company_phone_number && <div className="text-sm">{company.company_phone_number}</div>}
            </CardContent>
          </Card>

          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-800 text-lg">Billed To</CardTitle>
            </CardHeader>
            <CardContent className="pb-2">
              {customer ? (
                <>
                  <div className="font-semibold">
                    {getCustomerDisplayName()}
                  </div>
                  {getFormattedAddress(customer).length > 0 && (
                    <div className="text-sm text-gray-700">
                      {getFormattedAddress(customer).map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  )}
                  {/* Show VAT Reg No. for commercial customers - after address */}
                  {isCommercialCustomer && customer.vat_number && (
                    <div className="text-sm text-gray-600">
                      VAT Reg No.: {customer.vat_number}
                    </div>
                  )}
                  {getCustomerPhone() && (
                    <div className="text-sm">{getCustomerPhone()}</div>
                  )}
                  {getCustomerEmail() && (
                    <div className="text-sm">{getCustomerEmail()}</div>
                  )}
                </>
              ) : (
                <div className="text-gray-500 text-sm">No customer assigned</div>
              )}
            </CardContent>
          </Card>

          {/* Related Estimate - Use standard card */}
          {invoice.estimates && (
            <RelatedEstimatePreviewCard 
              estimate={invoice.estimates}
              compact={false}
              showCustomerInfo={false}
            />
          )}
        </div>

        {/* -- Right Column: Invoice Summary, Description, Financials, Line Items -- */}
        <div className="md:col-span-2 flex flex-col gap-6">
          <Card className="mb-2">
            <CardHeader>
              <CardTitle>{invoice.title}</CardTitle>
            </CardHeader>
            <CardContent>
              {invoice.description && <p className="mb-2">{invoice.description}</p>}
              {invoice.special_instructions && <div className="text-xs text-muted-foreground mb-1">
                <span className="font-bold">Special Instructions: </span>{invoice.special_instructions}
              </div>}
              {invoice.payment_terms && <div className="text-xs text-muted-foreground mb-1">
                <span className="font-bold">Payment Terms: </span>{invoice.payment_terms}
              </div>}
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Financial Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>{formatCurrency(invoice.subtotal || 0)}</span>
              </div>
              {showVat && (
                <div className="flex justify-between">
                  <span>VAT ({displayVatPercentage}%):</span>
                  <span>{formatCurrency(invoice.tax_amount || 0)}</span>
                </div>
              )}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total:</span>
                  <span>{formatCurrency(invoice.total_amount || 0)}</span>
                </div>
              </div>
              {invoice.paid_amount > 0 && (
                <>
                  <div className="flex justify-between text-green-600">
                    <span>Paid:</span>
                    <span>{formatCurrency(invoice.paid_amount || 0)}</span>
                  </div>
                  <div className="flex justify-between font-semibold">
                    <span>Balance:</span>
                    <span>{formatCurrency(((invoice.total_amount || 0) - (invoice.paid_amount || 0)))}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Line items */}
          <Card>
            <CardHeader>
              <CardTitle>Invoice Items</CardTitle>
            </CardHeader>
             <CardContent className="pb-6">
              {Array.isArray(lineItems) && lineItems.length > 0 ? (
                <>
                  {/* Mobile card view - hidden in PDF mode */}
                  {!pdfCompatible && (
                    <div className="sm:hidden space-y-3">
                      {lineItems.map((item) => (
                        <div key={item.id} className="border rounded-md p-3 space-y-2">
                          <div className="font-medium text-sm whitespace-normal break-words">
                            {item.description}
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <div>Unit: <span className="text-foreground">{getUnitLabel(item.base_unit)}</span></div>
                            <div>Qty: <span className="text-foreground">{item.quantity_invoiced}</span></div>
                            <div>Unit Cost: <span className="text-foreground font-mono">{formatCurrency(item.unit_cost)}</span></div>
                            <div>Total: <span className="text-foreground font-mono font-semibold">{formatCurrency(item.total_cost)}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Desktop table view - no responsive wrapper in PDF mode */}
                  <div className={pdfCompatible ? "overflow-x-auto" : "hidden sm:block overflow-x-auto"}>
                    <table className="table-auto w-full text-sm border">
                      <thead>
                        <tr>
                          <th className="border px-2 py-1">Description</th>
                          <th className="border px-2 py-1">Unit</th>
                          <th className="border px-2 py-1">Quantity</th>
                          <th className="border px-2 py-1">Unit Cost (R)</th>
                          <th className="border px-2 py-1">Total (R)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lineItems.map((item) => (
                          <tr key={item.id}>
                            <td className="border px-2 py-1">{item.description}</td>
                            <td className="border px-2 py-1">{getUnitLabel(item.base_unit)}</td>
                            <td className="border px-2 py-1">{item.quantity_invoiced}</td>
                            <td className="border px-2 py-1">{formatCurrency(item.unit_cost).replace('R', '')}</td>
                            <td className="border px-2 py-1">{formatCurrency(item.total_cost).replace('R', '')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground">No invoice items found.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default InvoicePreview;
